import type { ReactNode } from 'react'
import { Children, isValidElement } from 'react'
import { cn } from '@/utils/cn'

type Props = {
  children: ReactNode
  className?: string
  fluid?: boolean
  /** Vertical stack with consistent page gap (default true). */
  stack?: boolean
}

function isPageHeaderEl(child: ReactNode): boolean {
  return (
    isValidElement(child) &&
    typeof child.type !== 'string' &&
    Boolean((child.type as { isPageHeader?: boolean }).isPageHeader)
  )
}

/**
 * Full-height page shell: PageHeader stays edge-to-edge;
 * remaining content scrolls in a padded canvas (RFID_RE_FE parity).
 */
export function PageContainer({ children, className, fluid, stack = true }: Props) {
  const kids = Children.toArray(children)
  const header = kids.find(isPageHeaderEl)
  const body = header ? kids.filter((c) => c !== header) : kids

  return (
    <div className={cn('flex h-full min-h-0 w-full min-w-0 flex-col', className)}>
      {header ?? null}
      <div
        className={cn(
          'dashboard-canvas min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto',
          fluid
            ? 'px-3 py-4 sm:px-4 sm:py-5 lg:px-5'
            : 'mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8',
          stack && 'page-stack'
        )}
      >
        {body}
      </div>
    </div>
  )
}
